import {
  CommandBar,
  DirectionalHint,
  Dropdown,
  IDropdownOption,
  ITooltipProps,
  IconButton,
  Label,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  Text,
  TextField,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import * as React from 'react';
import {
  DataSource,
  DependentEntity,
  RuleSetTypeComplex,
  RuleSetTypeSingle,
} from '../../../../../Common/DataOwnerModels/RuleSetRecord';
import { DataOwnerRequestContext } from '../../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { useContext, useState } from 'react';
import { useId } from '@fluentui/react-hooks';
import * as styled from '../../DataOwnerManagement.styled';
import { RuleManagement } from './RuleManagement';
import { CDMRecord } from '../../../../../Common/DataOwnerModels/CDMRecord';
import * as Constants from '../../../../../Common/CommonModels/Constants';

export type RuleSetRequestPanelProps = {
  ruleSetId?: string;
  cdmDropdownOptionList: IDropdownOption[];
  isApprovedCdmListLoading: boolean;
  updateRuleSetStepLabel(ruleSetId: string, ruleSetNewName: string): void;
};

export function RuleSetRequestPanel(props: RuleSetRequestPanelProps): React.ReactElement {
  // Data Owner Request Context
  const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
  const environment = dataOwnerRequestDetails?.request?.environment;
  const currentRuleSetIndex = dataOwnerRequestDetails.ruleSets?.findIndex((x) => x.id == props.ruleSetId);
  const currentRuleSet = dataOwnerRequestDetails?.ruleSets[currentRuleSetIndex];

  // Field Data
  const joinTypeOptions: IDropdownOption[] = [
    { key: 'Inner Join', text: 'Inner Join' },
    { key: 'Outer Join', text: 'Outer Join' },
    { key: 'Left Outer Join', text: 'Left Outer Join' },
    { key: 'Right Outer Join', text: 'Right Outer Join' },
  ];

  const ruleSetTypeOptions: IDropdownOption[] = [
    { key: RuleSetTypeSingle, text: RuleSetTypeSingle },
    { key: RuleSetTypeComplex, text: RuleSetTypeComplex },
  ];

  // Form State
  const [sourceCdmError, setSourceCdmError] = useState<boolean>(false);

  // Tooltip State
  const rulesetNameTooltipId = useId('rulesetNameTooltip');
  const ruleSetTypeTooltipId = useId('ruleSetTypeTooltip');
  const sourceCdmTooltipId = useId('sourceCdmTooltip');
  const selectExpressionsTooltipId = useId('selectExpressionsTooltip');
  const dependentCdmTooltipId = useId('dependentCdmTooltip');
  const joinTypeTooltipId = useId('joinTypeTooltip');

  function onChangeName(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, { ...currentRuleSet, name: newValue });
    props.updateRuleSetStepLabel(currentRuleSet.id, newValue);
  }

  function onChangeDescription(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, { ...currentRuleSet, description: newValue });
  }

  function onChangeRuleSetType(event: React.FormEvent<HTMLElement>, newValue?: IDropdownOption) {
    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, { ...currentRuleSet, ruleSetType: newValue.text });
  }

  function onChangeSourceCdm(event: React.FormEvent<HTMLElement>, newValue?: IDropdownOption) {
    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
      ...currentRuleSet,
      dataSource: {
        entityTypeId: newValue.key.toString(),
      },
    });
  }

  function onChangeFilterExpression(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
    if (currentRuleSet.filterExpression == null || currentRuleSet.filterExpression?.compiledExpression == null) {
      dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
        ...currentRuleSet,
        filterExpression: {
          compiledExpression: { sql: newValue },
        },
      });
      return;
    }

    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
      ...currentRuleSet,
      filterExpression: {
        ...currentRuleSet.filterExpression,
        compiledExpression: { ...currentRuleSet.filterExpression.compiledExpression, sql: newValue },
      },
    });
  }

  function onChangeSelectExpressions(
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) {
    const newSelectExp: string[] = [];
    newValue?.split(Constants.RuleSet.ExpressionDelimiter).forEach((element) => {
      newSelectExp.push(element.trim());
    });
    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
      ...currentRuleSet,
      dataSource: { ...currentRuleSet.dataSource, selectExpressions: newSelectExp },
    });
  }

  // Informational Toop tip
  const onRenderLabelName = () => {
    return (
      <styled.LabelContainer>
        <Label>Rule Set Name</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={rulesetNameTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>This field holds Custom Rule Set Name, used for easy of identification.</Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            aria-describedby={rulesetNameTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const ruleSetTypeTooltipProps: ITooltipProps = {
    onRenderContent: () => {
      return (
        <styled.CustomTooltipContainer>
          <Text>Choose the suitable rule set type from the below:</Text>
          <Text>
            1. Single Rule Set Type: <br /> &emsp; This rule set type supports simple validations on selected Source CDM and defined column expressions on Source CDM.
          </Text>
          <Text>
            2. Complex Rule Set Type: <br /> &emsp; This rule set type supports complex join of Source and Dependent CDM based
            on the selected join type and defined join expression.
          </Text>
        </styled.CustomTooltipContainer>
      );
    },
  };

  const onRenderLabelRuleSetType = () => {
    return (
      <styled.LabelContainer>
        <Label>Rule Set Type</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={ruleSetTypeTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={ruleSetTypeTooltipProps}
        >
          <IconButton
            id={'RuleSetTypeHelpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={ruleSetTypeTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const sourceCdmTooltipProps: ITooltipProps = {
    onRenderContent: () => {
      return (
        <styled.CustomTooltipContainer>
          <Text>
            This source CDM field is the entity on which the rules defined in the rule set are directly executed. If
            dependent CDMs are specified, the defined rules will be applied to the cartesian product of join.
          </Text>
        </styled.CustomTooltipContainer>
      );
    },
  };

  const onRenderLabelSourceCdm = () => {
    return (
      <styled.LabelContainer>
        <Label>Source CDM</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={sourceCdmTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={sourceCdmTooltipProps}
        >
          <IconButton
            id={'SourceCdmHelpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={sourceCdmTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelSelectExps = () => {
    return (
      <styled.LabelContainer>
        <Label>Select Expressions</Label>
        <TooltipHost
          id={selectExpressionsTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field should be configured with column names or select expressions separated by delimiter |~| for
                    virtual table view of selected "CDM" with selected data columns. The defined rules will be applied
                    on the selected CDM View data.<br/>
                    Example 1: count(*) |~| column1, column2 <br/>
                    Example 2: * |~| COUNT(*) OVER (PARTITION BY personnelNumber, startDate, sequenceNumber) AS _duplicateCount
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'helpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={selectExpressionsTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelDependentCDMs = () => {
    return (
      <styled.LabelContainer>
        <Label>Dependent CDMs</Label>
        <TooltipHost
          id={dependentCdmTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    Add Dependent CDMs to configure an entity which will be joined with selected Source CDM and defined
                    rules in the rule set are executed on the cartesian product of join.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'dependentCdmHelpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={dependentCdmTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelDependentCDMName = () => {
    return (
      <styled.LabelContainer>
        <Label>Dependent CDM</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={dependentCdmTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This Dependent CDM field is the entity which will be joined with selected Source CDM and defined
                    rules in the rule set are executed on the cartesian product of join.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            id={'dependentCdmHelpIcon'}
            iconProps={{ iconName: 'Info' }}
            aria-describedby={dependentCdmTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelJoinType = () => {
    return (
      <styled.LabelContainer>
        <Label>Join Type</Label>
        <TooltipHost
          id={joinTypeTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field is used to choose the join type between the Source CDM and Dependent CDM.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            aria-describedby={joinTypeTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelTableAlias = () => {
    return (
      <styled.LabelContainer>
        <Label>Table Alias</Label>
        <TooltipHost
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field can be used to configure table alias for the selected Dependent CDM to utilize the alias
                    while defining join, select or filter expressions.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelJoinExp = () => {
    return (
      <styled.LabelContainer>
        <Label>Join Expression</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field should be populated with join conditions based on the chosen join type between the
                    selected Source CDM and Dependent CDM.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderLabelFilterExp = () => {
    return (
      <styled.LabelContainer>
        <Label>Filter Expression</Label>
        <TooltipHost
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>
                    This field can be populated with SQL filter conditions in order to filter the data of selected
                    Source CDM or cartesian product of Source and Dependent CDMs.
                  </Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const handleFocusOutSourceCdm = () => {
    if (currentRuleSet?.dataSource?.entityTypeId == null) {
      setSourceCdmError(true);
    } else {
      setSourceCdmError(false);
    }
  };

  const AddDependentCdm = () => {
    const newDependentEntity: DependentEntity = {
      entityTypeId: '',
      entitySource: '',
      onColumnExpression: '',
      joinType: '',
      tableAlias: '',
      selectExpressions: null,
    };

    if (currentRuleSet.dataSource == null) {
      const newDataSource: DataSource = {
        entityTypeId: '',
        dependentEntities: [newDependentEntity],
      };
      dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
        ...currentRuleSet,
        dataSource: newDataSource,
      });
      return;
    }

    if (currentRuleSet.dataSource.dependentEntities == null) {
      dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
        ...currentRuleSet,
        dataSource: {
          ...currentRuleSet.dataSource,
          dependentEntities: [newDependentEntity],
        },
      });
      return;
    }

    dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
      ...currentRuleSet,
      dataSource: {
        ...currentRuleSet.dataSource,
        dependentEntities: [...currentRuleSet.dataSource?.dependentEntities, newDependentEntity],
      },
    });
  };

  const getCdmDropdownOptionList = () => {
    let newApprovedCdmList: CDMRecord[] = [];

    // Create list of dropdown options for approved CDM list
    if (environment == 0 && dataOwnerRequestDetails.nonProdApprovedCdms != null) {
      newApprovedCdmList = JSON.parse(JSON.stringify(dataOwnerRequestDetails.nonProdApprovedCdms)); // deep copy
    } else if (environment == 1 && dataOwnerRequestDetails.prodApprovedCdms != null) {
      newApprovedCdmList = JSON.parse(JSON.stringify(dataOwnerRequestDetails.prodApprovedCdms)); // deep copy
    }
    const newCdmDropdownOptions: IDropdownOption[] = [];

    // Adding existing Approved CDMs
    for (const cdmRecord of newApprovedCdmList) {
      newCdmDropdownOptions.push({
        key: cdmRecord.id,
        text: cdmRecord.name,
      });
    }

    // Adding newly added CDMs from context
    for (const cdmRecord of dataOwnerRequestDetails.cdms) {
      newCdmDropdownOptions.push({
        key: cdmRecord.id,
        text: cdmRecord.name,
      });
    }
    return newCdmDropdownOptions;
  };

  return (
    <Pivot aria-label="Rule set Request wizard panel Pivots">
      <PivotItem
        headerText="Rule Set Details"
        data-testid="edit-req-ruleset-details-tab"
        headerButtonProps={{
          'data-order': 1,
          'data-title': 'Rule set Details',
        }}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <TextField
            data-testid="edit-request-ruleset-name"
            id="ruleSetName"
            onRenderLabel={onRenderLabelName}
            placeholder="Enter Custom Rule Set Name"
            value={currentRuleSet?.name}
            onChange={onChangeName}
            validateOnLoad={false}
            validateOnFocusOut={true}
          />
          <TextField
            data-testid="edit-request-ruleset-desc"
            label="Description"
            id="description"
            placeholder="Enter Custom Description"
            value={currentRuleSet?.description}
            onChange={onChangeDescription}
            validateOnLoad={false}
            validateOnFocusOut={true}
            multiline={true}
            autoAdjustHeight={true}
          />
          <Dropdown
            data-testid="edit-request-ruleset-type"
            onRenderLabel={onRenderLabelRuleSetType}
            id="ruleSetType"
            placeholder="Select Rule Set Type"
            ariaLabel="Rule Set Type"
            selectedKey={currentRuleSet?.ruleSetType}
            options={ruleSetTypeOptions}
            onChange={onChangeRuleSetType}
            multiSelect={false}
          />
          <Dropdown
            data-testid={'add-ruleset-sourceCdm-' + currentRuleSet.id}
            onRenderLabel={onRenderLabelSourceCdm}
            id="sourceCdm"
            placeholder="Select Source CDM"
            ariaLabel="Source CDM"
            selectedKey={currentRuleSet?.dataSource?.entityTypeId}
            options={getCdmDropdownOptionList()}
            onChange={onChangeSourceCdm}
            onBlur={handleFocusOutSourceCdm}
            errorMessage={sourceCdmError ? 'Please select Source CDM' : ''}
            multiSelect={false}
            disabled={props.isApprovedCdmListLoading}
          />
          <TextField
            data-testid="edit-request-ruleset-select-exp"
            onRenderLabel={onRenderLabelSelectExps}
            id="selectExpressions"
            placeholder="Enter column names or select expressions seperated by the delimiter |~|"
            defaultValue={currentRuleSet?.dataSource?.selectExpressions?.join(' ' + Constants.RuleSet.ExpressionDelimiter + ' ')}
            onChange={onChangeSelectExpressions}
            validateOnLoad={false}
            validateOnFocusOut={true}
            multiline={true}
            autoAdjustHeight={true}
          />
          {currentRuleSet.ruleSetType == RuleSetTypeComplex && (
            <>
              {onRenderLabelDependentCDMs()}
              {currentRuleSet?.dataSource?.dependentEntities?.map(
                (currentDependentEntity: DependentEntity, currentDependentEntityIndex: number) => (
                  <styled.BoxComponent>
                    <Stack tokens={{ childrenGap: 16 }}>
                      <styled.TableComponent>
                        <tbody>
                          <tr>
                            <td>{onRenderLabelDependentCDMName()}</td>
                            <td>
                              <CommandBar
                                items={[]}
                                farItems={[
                                  {
                                    key: 'cancel',
                                    iconProps: { iconName: 'Cancel' },
                                    onClick: (event, value) => {
                                      dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                        ...currentRuleSet,
                                        dataSource: {
                                          ...currentRuleSet.dataSource,
                                          dependentEntities: currentRuleSet.dataSource.dependentEntities.filter(
                                            (item, index) => index != currentDependentEntityIndex
                                          ),
                                        },
                                      });
                                    },
                                    buttonStyles: {
                                      root: {
                                        background: '#FAFAFA', //grey
                                      },
                                    },
                                  },
                                ]}
                                ariaLabel="Close action"
                                styles={{ root: { background: '#FAFAFA', paddingRight: 0 } }}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <Dropdown
                                data-testid={'add-request-ruleset-dependentCdm-field-' + currentDependentEntityIndex}
                                id="dependentCdm"
                                placeholder="Select Dependent CDM"
                                ariaLabel="Dependent CDM"
                                selectedKey={currentDependentEntity.entityTypeId}
                                options={props.cdmDropdownOptionList}
                                disabled={props.isApprovedCdmListLoading}
                                onChange={(event, newValue) => {
                                  const newDepEntitiesList: DependentEntity[] =
                                    currentRuleSet?.dataSource?.dependentEntities?.map((entity, index) => {
                                      if (index == currentDependentEntityIndex) {
                                        return { ...entity, entityTypeId: newValue.key.toString() };
                                      }
                                      return entity;
                                    });
                                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                    ...currentRuleSet,
                                    dataSource: {
                                      ...currentRuleSet.dataSource,
                                      dependentEntities: newDepEntitiesList,
                                    },
                                  });
                                }}
                                multiSelect={false}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <Dropdown
                                data-testid="add-ruleset-joinType"
                                onRenderLabel={onRenderLabelJoinType}
                                id="joinType"
                                placeholder="Select Join Type"
                                ariaLabel="Join Type"
                                selectedKey={currentDependentEntity.joinType}
                                options={joinTypeOptions}
                                onChange={(event, newValue) => {
                                  const newDepEntitiesList: DependentEntity[] =
                                    currentRuleSet?.dataSource?.dependentEntities?.map((entity, index) => {
                                      if (index == currentDependentEntityIndex) {
                                        return { ...entity, joinType: newValue.text };
                                      }
                                      return entity;
                                    });
                                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                    ...currentRuleSet,
                                    dataSource: {
                                      ...currentRuleSet.dataSource,
                                      dependentEntities: newDepEntitiesList,
                                    },
                                  });
                                }}
                                multiSelect={false}
                              />
                            </td>
                            <td>
                              <TextField
                                id="tableAlias"
                                onRenderLabel={onRenderLabelTableAlias}
                                placeholder="Enter Table Alias"
                                value={currentDependentEntity.tableAlias}
                                onChange={(event, newValue) => {
                                  const newDepEntitiesList: DependentEntity[] =
                                    currentRuleSet?.dataSource?.dependentEntities?.map((entity, index) => {
                                      if (index == currentDependentEntityIndex) {
                                        return { ...entity, tableAlias: newValue };
                                      }
                                      return entity;
                                    });
                                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                    ...currentRuleSet,
                                    dataSource: {
                                      ...currentRuleSet.dataSource,
                                      dependentEntities: newDepEntitiesList,
                                    },
                                  });
                                }}
                                validateOnLoad={false}
                                validateOnFocusOut={true}
                                multiline={false}
                                autoAdjustHeight={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <TextField
                                onRenderLabel={onRenderLabelJoinExp}
                                id="joinExpression"
                                placeholder="Enter Join Expression"
                                value={currentDependentEntity.onColumnExpression}
                                onChange={(event, newValue) => {
                                  const newDepEntitiesList: DependentEntity[] =
                                    currentRuleSet?.dataSource?.dependentEntities?.map((entity, index) => {
                                      if (index == currentDependentEntityIndex) {
                                        return { ...entity, onColumnExpression: newValue };
                                      }
                                      return entity;
                                    });
                                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                    ...currentRuleSet,
                                    dataSource: {
                                      ...currentRuleSet.dataSource,
                                      dependentEntities: newDepEntitiesList,
                                    },
                                  });
                                }}
                                //onGetErrorMessage={getDescriptionErrorMessage}
                                validateOnLoad={false}
                                validateOnFocusOut={true}
                                multiline={true}
                                autoAdjustHeight={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <TextField
                                onRenderLabel={onRenderLabelSelectExps}
                                id="dependentCdmselectexpressions"
                                placeholder="Enter comma separated column names or select expressions"
                                value={currentDependentEntity?.selectExpressions?.join(' '+Constants.RuleSet.ExpressionDelimiter+' ')}
                                onChange={(event, newValue) => {
                                  const newDepEntitiesList: DependentEntity[] =
                                    currentRuleSet?.dataSource?.dependentEntities?.map((entity, index) => {
                                      if (index == currentDependentEntityIndex) {
                                        const newSelectExp: string[] = [];
                                        newValue?.split(Constants.RuleSet.ExpressionDelimiter).forEach((element) => {
                                          //newSelectExp = newSelectExp.concat(element.trim());
                                          newSelectExp.push(element.trim());
                                        });
                                        return { ...entity, selectExpressions: newSelectExp };
                                      }
                                      return entity;
                                    });
                                  dataOwnerRequestDetails.updateRuleSetInList(currentRuleSetIndex, {
                                    ...currentRuleSet,
                                    dataSource: {
                                      ...currentRuleSet.dataSource,
                                      dependentEntities: newDepEntitiesList,
                                    },
                                  });
                                }}
                                validateOnLoad={false}
                                validateOnFocusOut={true}
                                multiline={true}
                                autoAdjustHeight={true}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </styled.TableComponent>
                    </Stack>
                  </styled.BoxComponent>
                )
              )}
              <PrimaryButton
                data-testid="add-ruleset-add-dependent-cdm-button"
                text="Add Dependent CDM"
                onClick={AddDependentCdm}
                allowDisabledFocus
              />
              <TextField
                onRenderLabel={onRenderLabelFilterExp}
                id="filterExpression"
                placeholder={'Enter SQL Expression'}
                value={currentRuleSet?.filterExpression?.compiledExpression?.sql}
                onChange={onChangeFilterExpression}
                validateOnLoad={false}
                validateOnFocusOut={true}
                multiline={true}
                autoAdjustHeight={true}
              />
            </>
          )}
        </Stack>
      </PivotItem>
      <PivotItem
        headerText="Rule Definitions"
        data-testid="edit-req-ruleset-rules-tab"
        itemCount={currentRuleSet?.rules?.length}
      >
        <RuleManagement ruleSetId={props.ruleSetId} />
      </PivotItem>
    </Pivot>
  );
}
